/**
 * Compact section / card pattern for desk pages that are NOT tables
 * (dashboards, settings, upload flows): a header row (title + optional
 * description left, actions right) over a 12–16px padded body.
 *
 *   <DeskSection title="Export" description="Send plays to Hudl" actions={…}>
 *     …
 *   </DeskSection>
 *
 * `variant="plain"` drops the card chrome (border/background) for sections
 * that sit directly on the page; `variant="card"` (default) is a bordered
 * surface. Use `.desk-grid` around several sections for a tight 12px grid.
 */
import { useId, type ReactNode } from 'react';

export interface DeskSectionProps {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  variant?: 'card' | 'plain';
  /** Heading level for the title; default 2. */
  headingLevel?: 2 | 3;
  className?: string;
  'data-testid'?: string;
}

export function DeskSection({
  title,
  description,
  actions,
  children,
  variant = 'card',
  headingLevel = 2,
  className,
  'data-testid': testId,
}: DeskSectionProps): JSX.Element {
  const headingId = useId();
  const Heading = headingLevel === 3 ? 'h3' : 'h2';
  return (
    <section
      className={`desk-section desk-section--${variant}${className ? ` ${className}` : ''}`}
      aria-labelledby={headingId}
      data-testid={testId}
    >
      <div className="desk-section__head">
        <div className="desk-section__text">
          <Heading className="desk-section__title" id={headingId}>
            {title}
          </Heading>
          {description === undefined || description === null ? null : (
            <p className="desk-section__description">{description}</p>
          )}
        </div>
        {actions === undefined || actions === null ? null : (
          <div className="desk-section__actions">{actions}</div>
        )}
      </div>
      {children === undefined || children === null ? null : (
        <div className="desk-section__body">{children}</div>
      )}
    </section>
  );
}
